import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface StaticPageProps {
    pageId: string;
    language?: string;
}

interface ContentSection {
    title: string;
    subtitle: string;
    description: string;
    buttonText: string;
    buttonLink: string;
    images: { url: string; alt: string; caption: string }[];
    items: { title: string; description: string; icon: string; link: string; image: string }[];
}

const StaticPage: React.FC<StaticPageProps> = ({ pageId, language = 'sv' }) => {
    const [sections, setSections] = useState<Record<string, ContentSection>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch(`/api/cms/${pageId}?language=${language}`)
            .then(res => res.ok ? res.json() : Promise.reject(`HTTP ${res.status}`))
            .then(data => {
                if (data.success) {
                    setSections(data.content || {});
                } else {
                    setError(data.error || 'Content not found');
                }
            })
            .catch(err => setError(String(err)))
            .finally(() => setLoading(false));
    }, [pageId, language]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin h-10 w-10 border-4 border-teal-500 border-t-transparent rounded-full" />
                    <span className="text-gray-500 dark:text-gray-400">Laddar...</span>
                </div>
            </div>
        );
    }

    if (error || Object.keys(sections).length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center p-8 max-w-md">
                    <span className="material-icons-round text-5xl text-gray-300 dark:text-gray-600 mb-3 block">article</span>
                    <h2 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">Sidan hittades inte</h2>
                    <p className="text-gray-500 dark:text-gray-400 mb-6">Innehållet för denna sida har inte skapats ännu. Kontakta administratören.</p>
                    <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium transition-colors">
                        <span className="material-icons-round text-sm">home</span>
                        Tillbaka till startsidan
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Content Sections */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 max-w-4xl">
                {Object.entries(sections).map(([sectionId, section]) => (
                    <div key={sectionId} className="mb-12 last:mb-0">
                        {/* Title */}
                        {section.title && (
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
                                {section.title}
                            </h1>
                        )}

                        {/* Subtitle */}
                        {section.subtitle && (
                            <h2 className="text-xl text-gray-600 dark:text-gray-400 mb-6 font-medium">
                                {section.subtitle}
                            </h2>
                        )}

                        {/* Description as rich content */}
                        {section.description && (
                            <div
                                className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: section.description.replace(/\n/g, '<br/>') }}
                            />
                        )}

                        {/* Images */}
                        {section.images && section.images.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                                {section.images.map((img, idx) => (
                                    <figure key={idx} className="rounded-xl overflow-hidden shadow-lg">
                                        <img src={img.url} alt={img.alt} className="w-full h-64 object-cover" />
                                        {img.caption && (
                                            <figcaption className="text-sm text-gray-500 dark:text-gray-400 p-3 bg-white dark:bg-gray-800">
                                                {img.caption}
                                            </figcaption>
                                        )}
                                    </figure>
                                ))}
                            </div>
                        )}

                        {/* Items */}
                        {section.items && section.items.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                {section.items.map((item, idx) => (
                                    <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md border border-gray-100 dark:border-gray-700">
                                        {item.icon && (
                                            <span className="material-icons-round text-teal-500 text-2xl mb-2 block">{item.icon}</span>
                                        )}
                                        {item.title && (
                                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                                        )}
                                        {item.description && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Button */}
                        {section.buttonText && section.buttonLink && (
                            <div className="mt-8">
                                <Link
                                    to={section.buttonLink}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold transition-colors shadow-lg shadow-teal-500/25"
                                >
                                    {section.buttonText}
                                    <span className="material-icons-round text-sm">arrow_forward</span>
                                </Link>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StaticPage;
